import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { saveTheme } from '../lib/settings'
import {
  applyResolvedTheme,
  normalizeThemePreference,
  readStoredTheme,
  resolveTheme,
  writeStoredTheme,
  type ResolvedTheme,
  type ThemePreference,
} from './theme'

/**
 * Tema durumu - uygulamanin tek kaynagi.
 *
 * `App.tsx`te router'in USTUNDE kuruluyor (StreamManager ile ayni gerekce):
 * tema navigasyondan bagimsiz, boylece sayfa gecislerinde sifirlanmiyor.
 */

interface ThemeContextValue {
  /** Kullanicinin SECTIGI deger (`system` dahil). */
  preference: ThemePreference
  /** Gercekten UYGULANAN deger (`system` cozumlenmis hali). */
  resolved: ResolvedTheme
  setTheme(next: ThemePreference): void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({
  serverTheme,
  children,
}: {
  /** `SessionProvider.refresh()`in okudugu sunucu degeri - `undefined` = henuz yuklenmedi. */
  serverTheme?: string | null
  children: ReactNode
}) {
  const [preference, setPreference] = useState<ThemePreference>(
    () => readStoredTheme() ?? 'system',
  )

  // Sunucu ACIKCA kaydedilmis bir deger donduruyorsa (giristen sonra)
  // cihaz/hafiza tahminini EZIYOR - ayni `applyServerLocale` mantigi.
  // `null`/gecersiz DEGERLENDIRILMIYOR: kullanicinin hesabina hic tema
  // kaydetmemis olmasi, yerel tercihini "system"e SIFIRLAMAK icin bir
  // sebep degil - `applyServerLocale`in `!code` durumunda hicbir sey
  // yapmadan cikmasiyla AYNI kural.
  useEffect(() => {
    const normalized = normalizeThemePreference(serverTheme)
    if (!normalized) return
    setPreference(normalized)
    writeStoredTheme(normalized)
  }, [serverTheme])

  const resolved = useMemo(() => resolveTheme(preference), [preference])

  useEffect(() => {
    applyResolvedTheme(resolved)
  }, [resolved])

  // `system` seciliyken cihaz tercihi degisirse (ör. isletim sistemi
  // gun/gece geçişi) yeniden cozumlemek icin re-render tetikle.
  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined' || !window.matchMedia) return
    const media = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => applyResolvedTheme(resolveTheme('system'))
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [preference])

  const setTheme = useCallback((next: ThemePreference) => {
    setPreference(next)
    writeStoredTheme(next)
    // Ag hatasi tema secimini GERI ALMIYOR: `LanguagePicker.saveLocale`
    // ile ayni politika - gorsel degisiklik kalici, yalnizca sunucuya
    // yazma sessizce basarisiz olabilir (bir sonraki basarili PUT'ta
    // yeniden denenir).
    void saveTheme(next).catch(() => undefined)
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setTheme }),
    [preference, resolved, setTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext)
  if (!value) throw new Error('useTheme, ThemeProvider icinde cagrilmali')
  return value
}
